import { useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { DollarSign, ArrowRightLeft } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";
import { getUserPreferredCurrency, getCurrencyFromCity } from "@/utils/currencyMapping";
import { useToast } from "../ui/use-toast";

interface CurrencyCalculatorProps {
  sourceCurrency?: string;
  targetCurrency?: string;
  exchangeRate?: number;
  onSourceCurrencyChange?: (currency: string) => void;
  destination?: string;
  respectUserPreference?: boolean;
}

const popularCurrencies = [
  { code: "USD", name: "US Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "GBP", name: "British Pound" },
  { code: "JPY", name: "Japanese Yen" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "AUD", name: "Australian Dollar" },
  { code: "CHF", name: "Swiss Franc" },
  { code: "CNY", name: "Chinese Yuan" },
];

export function CurrencyCalculator({
  sourceCurrency,
  targetCurrency,
  exchangeRate = 0.92,
  onSourceCurrencyChange,
  destination,
  respectUserPreference = false,
}: CurrencyCalculatorProps) {
  const { toast } = useToast();
  const [amount, setAmount] = useState<string>("100");
  const [convertedAmount, setConvertedAmount] = useState<string>("");
  const [currentExchangeRate, setCurrentExchangeRate] = useState<number>(exchangeRate);
  const [isReversed, setIsReversed] = useState<boolean>(false);
  
  const [fromCurrency, setFromCurrency] = useState<string>("");
  const [toCurrency, setToCurrency] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const initializeCurrencies = async () => {
      try {
        setIsLoading(true);
        let userCurrency = await getUserPreferredCurrency();
        let destCurrency = targetCurrency || "USD";
        
        if (destination) {
          const resolvedCurrency = await getCurrencyFromCity(destination);
          if (resolvedCurrency) {
            destCurrency = resolvedCurrency;
          }
        }
        
        let sourceToUse: string;
        
        if (respectUserPreference) {
          sourceToUse = userCurrency;
        } else if (sourceCurrency && onSourceCurrencyChange === undefined) {
          sourceToUse = sourceCurrency;
        } else {
          sourceToUse = userCurrency;
          if (onSourceCurrencyChange) {
            onSourceCurrencyChange(userCurrency);
          }
        }
        
        setFromCurrency(sourceToUse);
        setToCurrency(destCurrency);
        
        if (sourceToUse === destCurrency) {
          setCurrentExchangeRate(1);
        }
        
        calculateConversion("100");
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Currency initialization failed",
          description: "Could not load currency data. Using default values.",
        });
        
        setFromCurrency(sourceCurrency || "USD");
        setToCurrency(targetCurrency || "EUR");
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeCurrencies();
  }, [sourceCurrency, targetCurrency, destination, respectUserPreference]);

  const formatAmount = (value: number): string => {
    return value.toFixed(2);
  };

  const calculateConversion = (inputAmount: string, reversed: boolean = isReversed) => {
    const numAmount = parseFloat(inputAmount);
    if (isNaN(numAmount)) {
      setConvertedAmount("");
      return;
    }
    
    if (reversed) {
      setConvertedAmount(formatAmount(numAmount * (1 / currentExchangeRate)));
    } else {
      setConvertedAmount(formatAmount(numAmount * currentExchangeRate));
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAmount(value);
    calculateConversion(value);
  };

  const handleReverseConversion = () => {
    const newIsReversed = !isReversed;
    setIsReversed(newIsReversed);
    
    const tempCurrency = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(tempCurrency);
    
    setCurrentExchangeRate(1 / currentExchangeRate);
    calculateConversion(amount, newIsReversed);
    
    if (onSourceCurrencyChange) {
      onSourceCurrencyChange(toCurrency);
    }
  };

  const handleFromCurrencyChange = (value: string) => {
    setFromCurrency(value);
    if (onSourceCurrencyChange) {
      onSourceCurrencyChange(value);
    }
    try {
      localStorage.setItem('user_preferred_currency', value);
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      localStorage.clear();
    }
  };

  const handleToCurrencyChange = (value: string) => {
    setToCurrency(value);
  };

  useEffect(() => {
    if (fromCurrency && toCurrency) {
      calculateConversion(amount);
    }
  }, [currentExchangeRate, fromCurrency, toCurrency]);

  if (isLoading) {
    return <div className="p-4 text-center">Loading currency data...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col space-y-2">
        <Label htmlFor="amount">Amount</Label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </div>
          <Input
            id="amount"
            type="number"
            value={amount}
            onChange={handleAmountChange}
            className="pl-10"
            placeholder="Enter amount"
          />
        </div>
      </div>

      <div className="grid grid-cols-[1fr,auto,1fr] gap-2 items-center">
        <div>
          <Label htmlFor="fromCurrency">From</Label>
          <Select value={fromCurrency} onValueChange={handleFromCurrencyChange}>
            <SelectTrigger id="fromCurrency">
              <SelectValue placeholder="From Currency" />
            </SelectTrigger>
            <SelectContent>
              {popularCurrencies.map((currency) => (
                <SelectItem key={currency.code} value={currency.code}>
                  {currency.code} - {currency.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button 
          variant="ghost" 
          size="icon" 
          onClick={handleReverseConversion}
          className="mt-6"
        >
          <ArrowRightLeft className="h-4 w-4" />
        </Button>

        <div>
          <Label htmlFor="toCurrency">To</Label>
          <Select value={toCurrency} onValueChange={handleToCurrencyChange}>
            <SelectTrigger id="toCurrency">
              <SelectValue placeholder="To Currency" />
            </SelectTrigger>
            <SelectContent>
              {popularCurrencies.map((currency) => (
                <SelectItem key={currency.code} value={currency.code}>
                  {currency.code} - {currency.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-muted rounded-lg p-4 mt-4">
        <div className="text-sm text-muted-foreground mb-1">Converted Amount</div>
        <div className="text-2xl font-semibold">{convertedAmount} {toCurrency}</div>
        <div className="text-xs text-muted-foreground mt-2">
          Exchange Rate: 1 {fromCurrency} = {formatAmount(currentExchangeRate)} {toCurrency}
        </div>
      </div>

      <div className="text-xs text-muted-foreground mt-4">
        <p>Exchange rates are for informational purposes only and may vary at actual exchange services.</p>
      </div>
    </div>
  );
}
